/**
 * Channel Override Resolver — pure, dependency-free selection of which
 * price-affecting rules participate in the price calculation for a single
 * date, honoring exclusive channel_override rules.
 *
 * Used by THREE call sites that must stay in sync:
 *   1. supabase/functions/pricing-engine  (authoritative — mirrored port)
 *   2. webHotelierPricingBridge           (preview/push)
 *   3. pricingRulesCalculatorService      (calendar chips display)
 *
 * Keep this file free of Supabase / network / Date.now() so it can be unit
 * tested and ported to the Deno edge function verbatim.
 *
 * Semantics (agreed 2026-07-17):
 *   • channel_override rules stack additively with all other price rules
 *     (the "difference" the customer asked for: 100 +10% −20% → 90).
 *   • An EXCLUSIVE channel_override (conditions.exclusive === true) that
 *     matches the date silences every other price rule for that date; the
 *     single winner is the exclusive rule with the LOWEST priority number.
 *   • Exclusivity affects PRICE rules only — min_los rules, price limits
 *     and the €1 floor are applied by the callers regardless.
 */

// `exclusive` is typed as unknown (checked with === true) so every caller's
// rule shape is assignable: the frontend PricingRule stores conditions as
// Record<string, unknown>, the bridge's converted rules as a literal object.
export interface PriceRuleLike {
  id?: string;
  rule_type: string;
  priority: number;
  conditions?: { exclusive?: unknown };
}

export function isExclusiveChannelOverride(rule: PriceRuleLike): boolean {
  return (
    rule.rule_type === 'channel_override' && rule.conditions?.exclusive === true
  );
}

/**
 * Given the price rules that ALREADY MATCH the target date/room (callers run
 * date-range, scope and lead-time checks first — an exclusive rule for August
 * must never silence July), return the subset that participates in the
 * price calculation:
 *
 *   • no matching exclusive channel_override → all rules (additive stacking)
 *   • one or more matching exclusives → exactly ONE winner: the exclusive
 *     with the lowest priority number. All other rules are silenced.
 *
 * NOTE: a winner whose adjustment is 0 still silences the rest — that is a
 * feature ("freeze at base price for this period"), not a bug.
 */
export function selectPriceRules<T extends PriceRuleLike>(rules: T[]): T[] {
  const exclusives = rules.filter(isExclusiveChannelOverride);
  if (exclusives.length === 0) return rules;

  // Tie-break on equal priority: lowest id. The three call sites load rules
  // in different orders (DB query order differs between edge function and
  // browser), so array-order tie-breaking would let the mirrors disagree.
  const winner = exclusives.reduce((best, candidate) =>
    candidate.priority < best.priority ||
    (candidate.priority === best.priority &&
      (candidate.id ?? '') < (best.id ?? ''))
      ? candidate
      : best
  );
  return [winner];
}
